import {
  fetchShipmentParcels,
  handleCreateShipmentOrder,
  fetchRmaTicket,
  CONTACT_KEY,
  DEFAULT_CONTACT,
  MISSING_FIELD_LIST,
  SHIPPING_ENUM,
} from '@/entities/rma';
import { SIGNATURE_ENUM } from '@/shared/config';
import { handleFetchDivisions, showMessageError, handleFetchCountries } from '@/shared/lib';
import { useDebounceEffect, useSetState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDefaultState, getNotInLabelDevices } from '../../lib/createShippingLabel';

/**
 * Drives the create-shipping-label page for one RMA ticket, owning its draft shipment state and the form's handlers. Seeds state on mount from the ticket, countries, divisions and parcels, tracks whether required dates, dimensions and contact fields are missing, re-requests carrier rates on a debounced effect when the parcel, date, dimensions, contacts, signature or dangerous-goods inputs change, and exposes the field writers plus the missing-info, preview and edit-shipment toggles.
 *
 * @param DEFAULT_PACKAGE - Fallback parcel option preselected and prepended to the package list.
 * @returns Form state, header ref, dimension unit options and the page's handlers.
 */
const useCreateShippingLabel = (DEFAULT_PACKAGE: any) => {
  const { rmaId } = useParams();

  const navigate = useNavigate();

  const DIMENSIONS_UNIT_OPTIONS = [
    {
      label: 'inch',
      value: 'in',
    },
    {
      label: 'cm',
      value: 'cm',
    },
    {
      label: 'mm',
      value: 'mm',
    },
  ];
  const INITIAL_DIMENSIONS_STATE = {
    length: '',
    width: '',
    height: '',
    weight: '',
  };

  const [state, setState] = useSetState<any>({
    isLoadedData: true,
    isLoadedRate: true,
    rmaTicket: {},
    shipmentDate: dayjs(),
    package: DEFAULT_PACKAGE,
    distanceUnit: DIMENSIONS_UNIT_OPTIONS[0],
    senderContact: {
      ...DEFAULT_CONTACT,
    },
    recipientContact: {
      ...DEFAULT_CONTACT,
    },
    signatureConfirmation: '',
    dangerousGoods: true,
    listStatesUS: [],
    listStatesCA: [],
    listCountries: [],
    shipmentParcels: [],
    openEditShipmentModal: '',
    isOpenMissingModal: false,
    missingModalData: [],
    isOpenPreviewShipmentModal: false,
    selectedRate: null,
    isLoadingRates: false,
    selectedDevices: [],
    notInLabelDevices: [],
    notInLabelDeviceObjects: [],
  });

  const headerRef = useRef(null);

  /**
   * Indicates whether the shipping-label form still lacks data required to price a shipment. True when the shipment date or any dimension or weight field is empty, or when any required contact key (every CONTACT_KEY except street2 and company) is absent on the sender or the recipient contact.
   *
   * @returns True while required shipment data is missing.
   */
  const isMissingData = useMemo(() => {
    if (!state.shipmentDate || !state.length || !state.width || !state.height || !state.weight) {
      return true;
    }

    const listRequiredKeys = _.difference(_.values(CONTACT_KEY), ['street2', 'company']);

    /**
     * Indicates that at least one required contact field is absent from the sender or recipient contact. Evaluated with lodash some over the required contact keys, treating an empty value on either contact as invalid.
     *
     * @returns True when a required contact field is empty.
     */
    const isInvalidShipment = _.some(listRequiredKeys, (key) => {
      if (!state.recipientContact?.[key] || !state.senderContact?.[key]) {
        return true;
      }
      return false;
    });

    if (isInvalidShipment) {
      return true;
    }

    return false;
  }, [
    state.shipmentDate,
    state.length,
    state.width,
    state.height,
    state.weight,
    state.senderContact,
    state.recipientContact,
  ]);

  /**
   * Loads everything the shipping-label form needs for one RMA ticket and seeds the form state. Fetches the ticket, countries, CA and US divisions and shipment parcels in parallel, derives the default date, contacts and missing fields from the ticket, maps the parcels into dropdown options prefixed by the default package, collects the devices not yet on a label, and on failure logs, redirects to the RMA page and shows an error message.
   *
   * @returns Resolves once the form state is seeded or the redirect is issued.
   */
  const handleDataLoad = async () => {
    try {
      setState({ isLoadedData: false });
      const promises = [
        fetchRmaTicket(rmaId),
        handleFetchCountries(),
        handleFetchDivisions('CA'),
        handleFetchDivisions('US'),
        fetchShipmentParcels(),
      ];
      const [rmaTicket, listCountries, listStatesCA, listStatesUS, fetchedShipmentParcels] =
        await Promise.all(promises);
      const { shipmentDate, senderContact, recipientContact, missingModalData } =
        getDefaultState(rmaTicket);
      /**
       * Maps the shipment parcels fetched from the API into options for the package dropdown. Each option carries a JSX label with the parcel name, its length x width x height in the parcel's distance unit and its weight (falling back to '--' and 'lb'), plus the parcel id as both value and parcelId and the raw dimension fields.
       *
       * @returns The parcel options for the package select.
       */
      const formattedShipmentParcels = _.map(
        fetchedShipmentParcels,
        ({ name, id, weight, height, length, width, distanceUnit, weightUnit }) => ({
          label: (
            <div className="package-label">
              <div className="info">
                <div>{name}</div>
                <div className="info-size">
                  {`(${length} x ${width} x ${height} ${distanceUnit})`}
                </div>
              </div>
              <div className="info-weight">{`${weight || '--'} ${weightUnit || 'lb'}`}</div>
            </div>
          ),
          value: id,
          name,
          parcelId: id,
          weight,
          height,
          length,
          width,
          distanceUnit,
          weightUnit,
        }),
      );
      formattedShipmentParcels.unshift(DEFAULT_PACKAGE as any);

      const { notInLabelDevices, notInLabelDeviceObjects } = getNotInLabelDevices(
        rmaTicket.senderInformation,
      );

      const setStateObject = {
        isLoadedData: true,
        rmaTicket,
        shipmentDate,
        shipmentParcels: formattedShipmentParcels,
        senderContact,
        recipientContact,
        listCountries,
        listStatesCA,
        listStatesUS,
        missingModalData,
        isOpenMissingModal: !_.isEmpty(missingModalData),
        notInLabelDevices,
        notInLabelDeviceObjects,
      };
      setState(setStateObject);
    } catch (error) {
      console.error('Faield to load RMA ticket: ', error);
      navigate(`/rma/${rmaId}`, { replace: true });
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
  };

  /**
   * Requests carrier rates for the current shipment draft and preselects the cheapest one. Returns early while required data is missing, otherwise builds the shipment input from the RMA ticket, contacts, date and either the selected parcel id or the custom dimensions, calls the create-shipment-order mutation, stores the returned rates with the lowest-amount one selected, and on failure logs and clears the rates.
   *
   * @returns Resolves once rates are stored or cleared.
   */
  const handleRatesLoad = async () => {
    if (isMissingData) {
      return;
    }

    try {
      setState({ isLoadingRates: true });

      /**
       * Resolves which predefined parcel the rate request should reference. Evaluates to undefined when the selected package is 'Custom Dimensions', otherwise to the selected package's parcelId.
       *
       * @returns The parcel id, or undefined for custom dimensions.
       */
      const parcelId =
        state.package?.value === 'Custom Dimensions' ? undefined : state.package?.parcelId;
      const customDimensions = {
        length: Number(state.length),
        width: Number(state.width),
        height: Number(state.height),
        weight: Number(state.weight),
        distanceUnit: state.distanceUnit?.value,
        weightUnit: 'lb',
      };
      const input: any = {
        ticketId: state.rmaTicket.id,
        ticketType: 'RMA',
        shipmentDate: state.shipmentDate.toISOString(),
        senderContact: {
          ...state.senderContact,
        },
        recipientContact: {
          ...state.recipientContact,
        },
        parcelId,
        customDimensions,
        dangerousGoods: state.dangerousGoods,
      };
      if (state.signatureConfirmation) {
        input.signatureConfirmation = state.signatureConfirmation;
      }
      const shipmentOrder = await handleCreateShipmentOrder(input);
      const rates = shipmentOrder?.rates ?? [];
      /**
       * Picks the rate with the smallest amount among those returned with the shipment order. Derived with lodash minBy over the rates array, yielding undefined when that array is empty.
       *
       * @returns The lowest-amount rate, or undefined.
       */
      const cheapestRate = _.minBy(rates, (rate: any) => rate.amount);
      setState({
        rates,
        selectedRate: cheapestRate,
        isLoadingRates: false,
      });
    } catch (error) {
      console.error('Failed to fetch rates: ', error);
      setState({
        rates: [],
        selectedRate: null,
        isLoadingRates: false,
      });
    }
  };

  /**
   * Flips the visibility of the missing-information dialog. Writes the negated isOpenMissingModal flag back into state.
   *
   * @returns Nothing.
   */
  const toggleMissingModal = () => {
    setState({ isOpenMissingModal: !state.isOpenMissingModal });
  };

  /**
   * Closes the missing-information dialog and opens the edit-shipment modal on the side that needs attention. Targets the first sender edit step when the missing fields include the sender email, otherwise the first recipient edit step.
   *
   * @returns Nothing.
   */
  const onSubmitMissingModal = () => {
    setState({
      isOpenMissingModal: false,
      openEditShipmentModal: _.includes(state.missingModalData, MISSING_FIELD_LIST.SENDER_EMAIL)
        ? SHIPPING_ENUM.FIRST_EDIT_SENDER
        : SHIPPING_ENUM.FIRST_EDIT_RECIPIENT,
    });
  };

  /**
   * Flips the visibility of the shipment preview dialog. Writes the negated isOpenPreviewShipmentModal flag back into state.
   *
   * @returns Nothing.
   */
  const togglePreviewShipmentModal = () => {
    setState({ isOpenPreviewShipmentModal: !state.isOpenPreviewShipmentModal });
  };

  /**
   * Opens or closes the edit-shipment modal for one side of the shipment. Stores the passed value as openEditShipmentModal, or clears it to an empty string when the value is falsy.
   *
   * @param value - Edit step to open, or falsy to close.
   * @returns Nothing.
   */
  const toggleEditShipmentModal = (value: any) => {
    if (value) {
      setState({ openEditShipmentModal: value });
    } else {
      setState({ openEditShipmentModal: '' });
    }
  };

  /**
   * Merges edited address fields into either the sender or the recipient contact and closes the edit modal. Picks the target contact from which edit step is open, and when the sender was being edited for the first time schedules the recipient edit step to open 500ms later, matching the modal's animation.
   *
   * @param data - Edited contact fields to merge in.
   * @returns Nothing.
   */
  const updateShipment = (data: any) => {
    const key = [SHIPPING_ENUM.SENDER, SHIPPING_ENUM.FIRST_EDIT_SENDER].includes(
      state.openEditShipmentModal,
    )
      ? 'senderContact'
      : 'recipientContact';
    const isFirstEditSender = state.openEditShipmentModal === SHIPPING_ENUM.FIRST_EDIT_SENDER;
    setState({
      [key]: {
        ...state[key],
        ...data,
      },
      openEditShipmentModal: '',
    });
    if (isFirstEditSender) {
      // *: 500ms are animation time of edit shipment modal
      setTimeout(() => {
        setState({
          openEditShipmentModal: SHIPPING_ENUM.FIRST_EDIT_RECIPIENT,
        });
      }, 500);
    }
  };

  /**
   * Sets the date the shipment is scheduled for. Merges the given value into the form state under shipmentDate.
   *
   * @param date - Shipment date to store.
   * @returns Nothing.
   */
  const onChangeShipmentDate = (date: any) => {
    setState({ shipmentDate: date });
  };

  /**
   * Applies a package selection to the shipping-label form, either clearing the dimension fields or copying them from the chosen parcel. For 'Custom Dimensions' it resets length, width, height and weight to empty and stores the option under the given key, otherwise it stores the option and copies weight, length, width, height and distanceUnit from it, defaulting weight to an empty string.
   *
   * @param name - State key the selected option is stored under.
   * @param value - Value of the selected option.
   * @param option - Full parcel option carrying its dimensions.
   * @returns Nothing.
   */
  const handlePackageChange = (name: any, value: any, option: any) => {
    if (value === 'Custom Dimensions') {
      setState({
        ...INITIAL_DIMENSIONS_STATE,
        [name]: option,
      });
    } else {
      const { weight, length, width, height, distanceUnit } = option;
      setState({
        [name]: option,
        length,
        width,
        height,
        weight: weight || '',
        distanceUnit: {
          label: distanceUnit,
          value: distanceUnit,
        },
      });
    }
  };

  /**
   * Writes a single named field of the shipping-label form state. Calls the merge-state setter with an object holding only that key, leaving every other field untouched.
   *
   * @param name - State key to write.
   * @param value - Value stored under that key.
   * @returns Nothing.
   */
  const handleChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Stores the chosen signature-confirmation option on the shipment form. Writes value to signatureConfirmation and ignores the name argument.
   *
   * @param name - Field name from the option control, unused.
   * @param value - Signature confirmation level to store.
   * @returns Nothing.
   */
  const onChangeOptionSignature = (name: any, value: any) => {
    setState({ signatureConfirmation: value });
  };

  /**
   * Toggles signature confirmation for the shipment on or off. Clears signatureConfirmation when it already holds a value, otherwise sets it to the standard signature option.
   *
   * @returns Nothing.
   */
  const onChangeSignature = () => {
    if (state.signatureConfirmation) {
      setState({ signatureConfirmation: '' });
    } else {
      setState({ signatureConfirmation: SIGNATURE_ENUM.STANDARD });
    }
  };

  /**
   * Records which devices the user picked for the shipping label. Merges the given list into the form state under selectedDevices.
   *
   * @param selectedDevices - Devices chosen for the label.
   * @returns Nothing.
   */
  const handleDevicesSelect = (selectedDevices: any) => {
    setState({ selectedDevices });
  };

  useEffect(() => {
    handleDataLoad();
  }, [rmaId]);

  useDebounceEffect(() => {
    handleRatesLoad();
  }, [
    isMissingData,
    state.package,
    state.shipmentDate,
    state.length,
    state.width,
    state.height,
    state.weight,
    state.distanceUnit,
    state.senderContact,
    state.recipientContact,
    state.signatureConfirmation,
    state.dangerousGoods,
  ]);

  return {
    state,
    headerRef,
    isMissingData,
    DIMENSIONS_UNIT_OPTIONS,
    toggleMissingModal,
    onSubmitMissingModal,
    togglePreviewShipmentModal,
    toggleEditShipmentModal,
    updateShipment,
    onChangeShipmentDate,
    handlePackageChange,
    handleChange,
    onChangeOptionSignature,
    onChangeSignature,
    handleDevicesSelect,
  };
};

export { useCreateShippingLabel };
